import { useEffect, useRef, useReducer } from "react";

const initialState = {
  isReady: false,
  audioQueue: [],
  isPlaying: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_READY":
      return { ...state, isReady: true };
    case "ADD_TO_QUEUE":
      return { ...state, audioQueue: [...state.audioQueue, action.payload] };
    case "REMOVE_FROM_QUEUE":
      return { ...state, audioQueue: state.audioQueue.slice(1) };
    case "TOGGLE_IS_PLAYING":
      return { ...state, isPlaying: !state.isPlaying };
    case "STOP_PLAYING":
      return { ...state, audioQueue: [] };
    default:
      return state;
  }
};

const useAudioPlayer = (onEnded) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const onEndedRef = useRef(onEnded);

  const init = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
    }

    console.log("initializing :", audioContextRef.current);

    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume().then(() => {
        dispatch({ type: "SET_READY" });
      });
    } else {
      dispatch({ type: "SET_READY" });
    }
  };

  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (state.audioQueue.length === 0) {
      return;
    }

    const playNextAudioBuffer = () => {
      if (state.isPlaying) {
        return;
      }

      if (!state.isReady) {
        alert("Not ready, please init audioPlayer");
        return;
      }

      dispatch({ type: "TOGGLE_IS_PLAYING" });
      const audioBuffer = state.audioQueue[0];
      const audioContext = audioContextRef.current;
      if (sourceRef.current) {
        sourceRef.current.stop();
      }

      sourceRef.current = audioContext.createBufferSource();
      sourceRef.current.buffer = audioBuffer;
      sourceRef.current.connect(audioContext.destination);
      sourceRef.current.start();

      sourceRef.current.onended = () => {
        dispatch({ type: "TOGGLE_IS_PLAYING" });
        onEndedRef.current();
        dispatch({ type: "REMOVE_FROM_QUEUE" });
      };
    };

    playNextAudioBuffer();
  }, [state.audioQueue]);

  const playAudioBuffer = (audioBuffer) => {
    dispatch({ type: "ADD_TO_QUEUE", payload: audioBuffer });
  };

  const getAudioContext = () => {
    return audioContextRef.current;
  };

  const isReady = () => {
    return state.isReady;
  };

  const skip = (stopCurrentAudio = true) => {
    if (!sourceRef.current) {
      return;
    }
    if (stopCurrentAudio) {
      sourceRef.current.stop();
    }
    onEndedRef.current();
  };

  const stopPlaying = () => {
    sourceRef.current.stop();
    dispatch({ type: "STOP_PLAYING" });
    onEndedRef.current();
  };

  return { playAudioBuffer, init, getAudioContext, isReady, stopPlaying, skip };
};

export default useAudioPlayer;
